// © Microsoft Corporation. All rights reserved.

import { ConnectorNamespaces, SignedInStatus } from "@msnews/experiences-constants";
import { ExperiencesSharedStateActions, ExperiencesSharedStateConnector } from "@msnews/experiences-shared-state";
import {
    Market,
    Utility,
    appAnonCookieExistsCookieName,
    appEnvironment,
    getAppEnvironment,
    getCookie,
    getFromWebWorker,
    getHeadData,
    isWebWorkerPresent,
    sendToWebWorker,
    WebWorkerResponse
} from "@msnews/core";

import { PageBase } from "@msnews/experiences-page-base";
import { Telemetry } from "@msnews/telemetry-contracts";
import { AppAnonCookieResponseCachePayload, WebWorkerMessageId } from "@msnews/web-worker";
import { defaultOneServiceOcid } from "./AuthConstants";

/**
 * The resolver to indicate that the signInState has been fetched
 */
export let SignInStateResolver: () => void;

/**
 * A promise that will resolve when the signInState has been fetched
 */
const signInStateIsValid: Promise<void> = new Promise((resolve) => {
    SignInStateResolver = resolve;
});

/**
 * Attempt to get the APP_ANON cookie from web worker if present or by manually fetching
 * This should be called only once and from the page level, not by individual experiences
 * Must be called after the ExperiencesSharedStateConnector has been instantiated
 * @param errorHandler the error handler to respond to a failed fetch attempt
 * @param previousSignedInStatus (optional) the signedInStatus of the user at the time this function was called
 * @param bypassWebWorker (optional) flag indicating that webworker should be bypassed even if present
 * @returns the Promise
 */
export function getAppAnonCookie(errorHandler: (e: Error) => void, previousSignedInStatus?: SignedInStatus, bypassWebWorker?: boolean): Promise<void> {
    // If we are on the China ntp then do not make this call
    if (appEnvironment.CurrentMarket === Market.ZHCN && appEnvironment.HostPage.hostName.includes(".msn.cn")) {
        return;
    }

    // Attempt to fetch the APP_ANON cookie for personalized results
    // Get the app anon cookie fetch status from web worker cache.
    // When the web worker is not present or for some reason web worker did not fetch the app anon cookie, we continue making our own call.
    if (!bypassWebWorker && isWebWorkerPresent) {
        return getFromWebWorker<AppAnonCookieResponseCachePayload>({ id: WebWorkerMessageId.AppAnonCookie }).then((wwAppAnonResponse) => {
            handleSignInResponse(wwAppAnonResponse, previousSignedInStatus);
        });
    } else {
        return fetchAppAnonCookieAsync().then(() => {
            handleSignInResponse(null, previousSignedInStatus);
        }).catch((e) => {
            handleSignInResponse();
            errorHandler(e);
        });
    }
}

/**
 * get user signed in status
 */
export function isUserSignedIn(): boolean {
    // for PrimeHybrid
    const headDataSignInStatus = getHeadData().UserIsSignedIn;
    if (headDataSignInStatus !== undefined && headDataSignInStatus !== null) {
        return headDataSignInStatus;
    }

    // for Peregine
    const dataConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    const signInState: SignedInStatus = dataConnector ? dataConnector.getCurrentState().signedIn : SignedInStatus.NotSignedIn;
    return signInState === SignedInStatus.SignedIn;
}

/**
 * Returns the current user sign in state
 * This returns the currently known sign-in state and does not wait for the sign in state to be updated
 */
export async function getUserSignInState(): Promise<SignedInStatus> {
    const dataConnector: ExperiencesSharedStateConnector = await PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    const signInState: SignedInStatus = dataConnector ? dataConnector.getCurrentState().signedIn : SignedInStatus.NotSignedIn;
    return signInState;
}

/**
 * Waits until the user sign in state has been set, then resolves with the current sign in state.
 */
export async function waitForSignInState(): Promise<SignedInStatus> {
    await signInStateIsValid;

    const dataConnector: ExperiencesSharedStateConnector = await PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    const signInState: SignedInStatus = dataConnector ? dataConnector.getCurrentState().signedIn : SignedInStatus.NotSignedIn;
    return signInState;
}

/**
 * Calls into <domain>/spartan/getappanoncookie to set the APP_ANON cookie
 * If the APP_ANON and aace cookies do not already exist this will set the APP_ANON and aace cookies
 * If the APP_ANON cookie exists but the user is not signed in this will delete the APP_ANON and aace cookies
 * This function should typically not be called outside this library unless you are trying to fetch the APP_ANON cookie manually
 * @return {Promise<string>} - The aace cookie value, or null if aace is not fetched
 */
export async function fetchAppAnonCookieAsync(): Promise<string> {

    // we await the following fetches but we do not read their return values because the relevant sign-on information is stored in the auth cookies
    if (appEnvironment.UseOneServiceAuthEndpoints) {
        await fetch(`https://api.msn.com/auth/cookie/appanon?scn=app_anon&apikey=${appEnvironment.OneServiceApiKey}&ocid=${defaultOneServiceOcid}`,
            { credentials: "include" });
    } else {
        const hostPage = getAppEnvironment().HostPage;
        const hostName = hostPage && hostPage.hostName;
        const host = (hostName === "int1.msn.com" || hostName === "int.msn.com") ? hostName : "www.msn.com";
        const marketLocale = getHeadData().Locale;

        await fetch(`https://${host}/spartan/${marketLocale}/getappanoncookie`, { credentials: "include" });
    }

    return getCookie(appAnonCookieExistsCookieName);
}

/**
 * Handle the response to fetching the APP_ANON cookie
 * @param wwAppAnonResponse (optional) the response from the Web Worker
 * @param previousSignedInStatus (optional) the signedInStatus of the user at the time getAppAnonCookie() was called
 */
function handleSignInResponse(wwAppAnonResponse?: WebWorkerResponse<AppAnonCookieResponseCachePayload>, previousSignedInStatus?: SignedInStatus): void {
    // Resolve the sign in state awaiter.
    SignInStateResolver();

    // If "aace" cookie exists that implies "lt" or "at" token was authenticated and user is signedIn, otherwise user is not signedIn
    const appAnonCookieExistsValue: string = getCookie(appAnonCookieExistsCookieName);
    const signedInStatus: SignedInStatus = (appAnonCookieExistsValue !== null) ? SignedInStatus.SignedIn : SignedInStatus.NotSignedIn;

    // Trigger my feed and Pdp update when user goes from signed out to signed in state
    if (isWebWorkerPresent
        && wwAppAnonResponse
        && wwAppAnonResponse.payload
        && !wwAppAnonResponse.payload.appAnonCookieExists
        && signedInStatus === SignedInStatus.SignedIn) {
        sendToWebWorker({ id: WebWorkerMessageId.UpdateMyFeed });
        sendToWebWorker({ id: WebWorkerMessageId.Updatepdp });
        sendToWebWorker({ id: WebWorkerMessageId.UpdateMyBriefing });
    }

    // Call action to update signedInStatus if the user signedInStatus was updated by the fetch call
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    const signInState: SignedInStatus = sharedStateConnector.getCurrentState().signedIn;
    if (signInState !== signedInStatus) {
        ExperiencesSharedStateActions.signedInStatus.getActionSender(sharedStateConnector).send(signedInStatus);
    }

    // If the user has signed in or out since the previous impression was fired, fire a new impression event
    if (Utility.isDefined(previousSignedInStatus)
        && previousSignedInStatus !== signedInStatus) {
        Telemetry.addOrUpdateTmplProperty("signInUpdate", "1");
    }
}
